import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';

const PUBLIC_URL = process.env.PUBLIC_URL || '';

export const ADMIN_HERO_BACKGROUND = 'hero-banner-1.jpg';
export const APPRENANT_HERO_BACKGROUND = 'hero-banner-2.jpg';
export const FORMATEUR_HERO_BACKGROUND = 'hero-banner-3.jpg';

export type AdminPageHeroSideLink = {
  label: string;
  to: string;
  icon: string;
};

/** Carte latérale du hero : libellé, valeur principale et texte d’appui (contexte page). */
export type AdminPageHeroSideCard = {
  label: string;
  value: string;
  helper?: string | null;
  sideLinks?: AdminPageHeroSideLink[];
};

export type AdminPageHeroProps = {
  title: string;
  description?: string | null;
  primaryAction?: ReactNode;
  sideCard?: AdminPageHeroSideCard | null;
  /** Fichier sous `public/images/` (ex. `hero-banner-2.jpg`). */
  backgroundImage?: string;
  /** `large` : pages vitrine publiques ; `default` : portails connectés. */
  size?: 'default' | 'large';
};

/**
 * Bannière hero admin (gradient + image) avec carte latérale contextuelle.
 * Utiliser via `AdminPageShell` en passant `sideCard` adapté à chaque page.
 */
export default function AdminPageHero({
  title,
  description = null,
  primaryAction,
  sideCard,
  backgroundImage = ADMIN_HERO_BACKGROUND,
  size = 'default',
}: AdminPageHeroProps) {
  const hasSideCard = Boolean(sideCard);
  const { label, value, helper, sideLinks = [] } = sideCard ?? {
    label: title,
    value: '—',
    helper: null,
    sideLinks: [],
  };
  const isLarge = size === 'large';
  const panelMinHeight = isLarge ? 'min-h-64 sm:min-h-72' : 'min-h-48';
  const bannerPadding = isLarge ? 'p-10' : 'p-8';
  const titleClass = isLarge ? 'text-3xl sm:text-[2rem]' : 'text-2xl';
  const descriptionClass = isLarge ? 'text-base line-clamp-4' : 'text-sm line-clamp-2';
  const contentMaxW = isLarge ? 'max-w-xl' : 'max-w-[320px]';
  const sidePadding = isLarge ? 'p-8' : 'p-6';
  const sideValueLength = value.trim().length;
  const sideValueDensity =
    sideValueLength > 22 ? 'admin-page-hero__side-value--xs' : sideValueLength > 16 ? 'admin-page-hero__side-value--sm' : '';

  return (
    <div className="grid grid-cols-12 items-stretch gap-6">
      <div
        className={`relative col-span-12 overflow-hidden rounded-xl ${hasSideCard ? 'lg:col-span-8' : ''} ${panelMinHeight}`}
      >
        <div className={`absolute inset-0 z-10 flex items-center justify-between ${bannerPadding}`}>
          <div className={contentMaxW}>
            <h3 className={`${titleClass} font-bold leading-tight text-white`}>{title}</h3>
            {description ? (
              <p
                className={`mt-3 ${descriptionClass} leading-relaxed text-white/80`}
                title={description}
              >
                {description}
              </p>
            ) : null}
            {primaryAction ? <div className="mt-5">{primaryAction}</div> : null}
          </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-dark via-primary/95 to-primary/50" />
        <img
          className="absolute inset-0 h-full w-full object-cover opacity-40 mix-blend-overlay"
          src={`${PUBLIC_URL}/images/${backgroundImage}`}
          alt=""
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>

      {hasSideCard ? (
      <div
        className={`admin-page-hero__side-card relative col-span-12 flex min-w-0 flex-col justify-between overflow-hidden rounded-xl bg-primary-dark text-white shadow-lg lg:col-span-4 ${panelMinHeight} ${sidePadding}`}
      >
        <div className="relative z-10 min-w-0">
          <p className={`font-medium text-white/70 ${isLarge ? 'text-base' : 'text-sm'}`}>{label}</p>
          <h4
            className={`admin-page-hero__side-value mt-1 font-bold ${sideValueDensity} ${isLarge ? 'admin-page-hero__side-value--large' : ''}`}
            title={value}
          >
            {value}
          </h4>
          {helper ? (
            <p
              className="admin-page-hero__side-helper mt-2 line-clamp-2 text-xs text-white/70"
              title={helper}
            >
              {helper}
            </p>
          ) : null}
        </div>
        {sideLinks.length > 0 ? (
          <div className="relative z-10 flex gap-3">
            {sideLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-white/20 px-3 py-2 text-xs font-bold backdrop-blur-md transition hover:bg-white/30"
              >
                <LucideSymbolIcon name={link.icon} tone="inherit" className="text-white" size={14} />
                {link.label}
              </Link>
            ))}
          </div>
        ) : null}
        <div className="absolute -bottom-10 -right-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
      </div>
      ) : null}
    </div>
  );
}
